import React, { useState, useEffect, useRef } from "react";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import _ from "lodash";
import { numberFormat } from "../../util/currency-formatter";
import { disableArrows, parseNumericValue } from "../../util/common";
import { Decimal, roundValue, toDecimal } from "src/util/decimal-utils";

/**
 * CustomInput
 * A general-purpose input component used throughout the app.
 *
 * Props (high level):
 * - name: input name attribute used in change events
 * - type: input type (e.g., "text", "number")
 * - label / placeholder: displayed label text
 * - value: controlled value for the input
 * - onChange/onBlur: event handlers
 * - formatDecimals: when true and type === "number", formats value on blur using numberFormat
 * - validation/required: used to show a required marker
 * - disabled, className, customInputClass, dataCy and ...props are forwarded to the input element
 *
 * Behavior / notes:
 * - Uses controlled `value` prop (falls back to empty string to avoid uncontrolled warnings).
 * - If `formatDecimals` is true and `type` is "number", the value is formatted on blur with `numberFormat`
 *   and the formatted value is emitted through onChange. onBlur (if provided) is still called.
 * - Shows an error message when `error` and `touched` are truthy (and input is not disabled).
 */
export default function CustomInput({
  name,
  type = "text",
  label,
  touched,
  valid,
  placeholder,
  value,
  error,
  required,
  onChange,
  onBlur,
  disabled,
  className = "",
  validation = [],
  customInputClass = "",
  formatDecimals = false,
  dataCy = "",
  ...props
}) {
  let isRequired =
    required ||
    (validation.includes("required") &&
      !(validation.includes("skip") || validation.includes("optional")));

  const handleBlur = (e) => {
    if (
      formatDecimals &&
      type === "number" &&
      e.target.value !== "" &&
      e.target.value !== undefined
    ) {
      const formattedValue = numberFormat(e.target.value, true);
      e.target.value = formattedValue;
      const event = { ...e, target: { ...e.target, name: e.target.name } };
      onChange(event);
    }
    if (onBlur) onBlur(e);
  };

  return (
    <div className="form-group">
      <div
        className={_.uniq(["input-block", ...className.split(" ")]).join(" ")}
      >
        {(label || placeholder) && (
          <label className="input-label">
            <span>
              {label || placeholder || ""}{" "}
              {isRequired ? <span className="text-danger">&nbsp;*</span> : null}
            </span>
          </label>
        )}
        <input
          className={_.uniq([
            "form-control-input",
            ...(disabled ? ["disabled"] : []),
            ...customInputClass.split(" "),
          ]).join(" ")}
          name={name}
          type={type}
          value={value ?? ""} //this will avoid rendering the data on every action being done on this input and only round off on blur.
          touched={(touched || "").toString()}
          error={(error || "").toString()}
          disabled={disabled}
          onChange={onChange}
          onBlur={formatDecimals ? handleBlur : onBlur}
          step={formatDecimals ? "0.01" : undefined}
          data-cy={dataCy}
          {...props}
        />
        {!disabled && error && touched && (
          <small className="float-start text-danger">{error}</small>
        )}
      </div>
    </div>
  );
}

export function AmountInput({
  name,
  type = "text",
  label,
  touched,
  valid,
  placeholder,
  value,
  error,
  required,
  onChange,
  disabled,
  className = "",
  validation = [],
  dataCy = "",
  ...props
}) {
  let isRequired =
    required ||
    (validation.includes("required") &&
      !(validation.includes("skip") || validation.includes("optional")));

  /**
   * AmountInput
   * A styled input for currency/amounts that includes a money icon.
   *
   * Props:
   * - name, type, label, placeholder, value, onChange, disabled, validation, dataCy, className
   * - validation/required determine whether to show the required marker
   *
   * Behavior:
   * - Renders an AttachMoney icon to the left of the input.
   * - Error text is shown when `error` is truthy and input is not disabled.
   * - This component does not perform formatting itself — formatting should be handled by the
   *   parent (so the `value` prop can be a formatted string or raw number as needed).
   */
  return (
    <div className="form-group">
      <div
        className={_.uniq([
          "input-block",
          "amount-block",
          ...className.split(" "),
        ]).join(" ")}
      >
        {(label || placeholder) && (
          <label className="input-label d-flex">
            <>
              {label || placeholder || ""}{" "}
              {isRequired ? <span className="text-danger">&nbsp;*</span> : null}
            </>
          </label>
        )}
        <span className="input-icon">
          <AttachMoneyIcon />
        </span>
        <input
          className={`form-control-input ${label || placeholder
            ? ""
            : !disabled && error
              ? "border-danger"
              : ""
            }`}
          name={name}
          type={type}
          value={value}
          touched={(touched || "").toString()}
          error={(error || "").toString()}
          disabled={disabled}
          onChange={onChange}
          data-cy={dataCy}
          {...props}
        />
        {!disabled && error && (
          <small className="float-start text-danger">{error}</small>
        )}
      </div>
    </div>
  );
}

export function LockInput({
  name,
  lock,
  type = "text",
  label,
  touched,
  valid,
  placeholder,
  value,
  error,
  required,
  onChange,
  onBlur,
  onFocus,
  disabled,
  className = "",
  validation = [],
  formatDecimals = false,
  decimalPlaces = 2,
  selectOnFocus = false,
  dataCy = "",
  ...props
}) {
  let isRequired =
    required ||
    (validation.includes("required") &&
      !(validation.includes("skip") || validation.includes("optional")));

  const shouldHandleDecimals = formatDecimals;
  const [inputValue, setInputValue] = useState(value ?? "");
  const hasUserTyped = useRef(false);

  /**
   * LockInput
   * Input with a leading `lock` element (arbitrary React node) and optional decimal formatting.
   *
   * Props:
   * - lock: React node rendered before the input (often a label or icon)
   * - type, name, label, value, onChange, onBlur, disabled, validation
   * - formatDecimals: when true and type === "number", the component will round/format values
   * - decimalPlaces: number of decimal places used for `step` and formatting (default 2)
   *
   * Behavior / notes:
   * - Preserves partial decimal strings such as "1." while the user is typing.
   * - On blur it parses and formats the numeric value, updates the event target and calls onChange.
   * - Uses `disableArrows` onKeyDown to avoid arrow-based number changes.
   */
  useEffect(() => {
    if (!shouldHandleDecimals || hasUserTyped.current) return;

    if (value === undefined || value === null) {
      setInputValue("");
      return;
    }

    if (typeof value === "number" && !Number.isNaN(value)) {
      setInputValue(roundValue(value, decimalPlaces).toFixed(decimalPlaces));
      return;
    }

    setInputValue(String(value));
  }, [decimalPlaces, shouldHandleDecimals, value]);

  const handleChange = (e) => {
    if (!shouldHandleDecimals) {
      onChange(e);
      return;
    }

    const nextValue = e.target.value;
    hasUserTyped.current = true;

    if (nextValue === "") {
      setInputValue("");
      onChange({ ...e, target: { ...e.target, name, value: "" } });
      return;
    }

    if (/^[0-9]*\.?[0-9]*$/.test(nextValue)) {
      const parts = nextValue.split(".");
      if (parts.length === 1 || parts[1].length <= decimalPlaces) {
        setInputValue(nextValue);
        onChange({ ...e, target: { ...e.target, name, value: nextValue } });
      }
    }
  };

  const handleBlur = (e) => {
    if (!shouldHandleDecimals) {
      if (onBlur) onBlur(e);
      return;
    }

    const decimalValue = toDecimal(e.target.value);
    const roundedAsString = roundValue(decimalValue, decimalPlaces).toFixed(decimalPlaces);
    const normalizedValue = parseNumericValue(numberFormat(roundedAsString, true));
    const normalizedEvent = {
      ...e,
      target: { ...e.target, name, value: normalizedValue },
    };

    setInputValue(roundedAsString);
    hasUserTyped.current = false;
    onChange(normalizedEvent);
    if (onBlur) onBlur(normalizedEvent);
  };

  const handleFocus = (e) => {
    if (selectOnFocus && !disabled && typeof e.target.select === "function") {
      e.target.select();
    }

    if (onFocus) onFocus(e);
  };

  return (
    <div className="form-group">
      <div
        className={_.uniq([
          "input-block",
          "lock-input",
          ...className.split(" "),
        ]).join(" ")}
      >
        {lock}
        {(label || placeholder) && (
          <label className="input-label">
            <span>
              {label || placeholder || ""}{" "}
              {isRequired ? <span className="text-danger">*</span> : null}
            </span>
          </label>
        )}
        <input
          className={`form-control-input text-right ${label || placeholder
            ? ""
            : !disabled && error && (touched || typeof touched == "undefined")
              ? "border-danger"
              : ""
            }`}
          name={name}
          type={shouldHandleDecimals ? "text" : type}
          value={shouldHandleDecimals ? inputValue : value}
          touched={(touched || "").toString()}
          error={(error || "").toString()}
          disabled={disabled}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={disableArrows}
          onBlur={handleBlur}
          inputMode={shouldHandleDecimals ? "decimal" : undefined}
          pattern={shouldHandleDecimals || type == "number" ? "[0-9]*.?[0-9]*" : ""}
          step={`0.${"0".repeat(decimalPlaces - 1)}1`}
          data-cy={dataCy}
          {...props}
        />
        {!disabled && error && touched && (
          <small className="float-start text-danger">{error}</small>
        )}
      </div>
    </div>
  );
}

export function NumberInput({
  name,
  label,
  placeholder,
  value,
  error,
  touched,
  onChange,
  onBlur,
  disabled,
  required,
  className = "",
  validation = [],
  customInputClass = "",
  decimalPlaces = 2,
  allowNegative = false,
  dataCy = "",
  ...props
}) {
  /**
   * NumberInput
   * A controlled numeric input that allows the user to type decimal numbers freely and only
   * normalizes/rounds the value on blur.
   *
   * Props:
   * - name, label, placeholder, value, error, touched, onChange, onBlur, disabled
   * - decimalPlaces: used to set the `step` attribute and influence blur rounding
   * - customInputClass/className/dataCy forwarded to input
   *
   * Behavior / implementation details:
   * - Keeps an internal `inputValue` state to allow partial numeric inputs (e.g. "12.").
   * - `hasUserTyped` ref prevents overwriting the user's in-progress typing when `value` updates
   *   from the parent; when the user blurs, the component rounds/parses the value and calls
   *   onChange with the normalized numeric value.
   * - Only allows characters that match the regex /^[0-9]*\.?[0-9]*$/ while typing.
   */
  const [inputValue, setInputValue] = useState(value ?? "");
  const hasUserTyped = useRef(false);
  const normalizedLabel = `${label || placeholder || name || ""}`
    .trim()
    .toLowerCase();
  const isQuantityField =
    normalizedLabel.includes("quantity") || normalizedLabel.includes("qty");
  // Always use text input for full control over decimal behavior
  const inputType = "text";

  useEffect(() => {
    if (
      !hasUserTyped.current &&
      value !== undefined &&
      value !== null &&
      !isNaN(value)
    ) {
      const decimalValue = toDecimal(value);
      const rounded = roundValue(decimalValue, 2);
      setInputValue(rounded.toFixed(2)); // Force 2 decimal places display
    }
  }, [value]);

  /**
   * handleStep - Increments or decrements the integer part while preserving decimals
   *
   * This function is called when the user clicks the + or - buttons for quantity fields.
   * It extracts the integer and decimal parts of the current value, modifies only the
   * integer part by the specified direction (+1 or -1), and preserves the existing
   * decimal digits.
   *
   * @param {number} direction - The step direction: +1 for increment, -1 for decrement
   *
   * Behavior:
   * - Prevents values from going below 1 (minimum value enforcement)
   * - Preserves decimal places when stepping (e.g., 8.25 → 9.25)
   * - Pads decimals to match decimalPlaces setting
   * - Updates both local state and notifies parent via onChange
   *
   */
  const handleStep = (direction) => {
    const currentValue = toDecimal(inputValue || "0");

    // Extract sign, integer, and decimal parts
    const isNegative = currentValue.isNegative();
    const absValue = currentValue.abs();

    // Split into integer and decimal parts
    const absString = absValue.toString();
    const parts = absString.split('.');
    const intPart = parseInt(parts[0] || '0', 10);
    const decPart = parts[1] || '0'.repeat(decimalPlaces);

    let newIntPart;
    let newIsNegative = isNegative;

    if (direction === -1) {
      // Clicking minus button
      if (!isNegative) {
        // Positive or zero: subtract 1 from integer part
        newIntPart = intPart - 1;
        if (newIntPart < 0) {
          // Crossed zero, flip to negative and adjust
          newIntPart = Math.abs(newIntPart);
          newIsNegative = true;
        }
      } else {
        // Already negative: increase magnitude
        newIntPart = intPart + 1;
        newIsNegative = true;
      }
    } else {
      // Clicking plus button (direction === 1)
      if (isNegative) {
        // Negative: decrease magnitude
        newIntPart = intPart - 1;
        if (newIntPart < 0) {
          // Crossed zero, flip to positive
          newIntPart = Math.abs(newIntPart);
          newIsNegative = false;
        }
      } else {
        // Positive: add 1 to integer part
        newIntPart = intPart + 1;
      }
    }

    // Reassemble the value with preserved decimals
    const newAbsValue = toDecimal(`${newIntPart}.${decPart}`);
    const newValue = newIsNegative ? newAbsValue.negated() : newAbsValue;

    // Check if we should block negative values
    if (!allowNegative && newValue.lessThan(0)) {
      return;
    }

    const newValueStr = newValue.toFixed(decimalPlaces);
    setInputValue(newValueStr);
    onChange({ target: { name, value: newValueStr } });
  };

  /**
   *
   * This function handles real-time input validation while allowing users to type
   * partial numeric values (e.g., "12.", "12.5"). It enforces numeric-only input
   * with optional decimal point and limits decimal places.
   *
   * @param {Event} e - The input change event
   *
   * Validation rules:
   * - Only allows digits (0-9) and one optional decimal point
   * - Allows empty input (user can clear the field)
   * - Allows partial inputs like "12." or "12.5" for better UX
   * - Enforces maximum decimal places (won't allow typing more decimals than decimalPlaces)
   * - Rejects multiple decimal points
   *
   * Example accepted inputs during typing:
   * - "8" → valid
   * - "8." → valid (partial)
   * - "8.2" → valid
   * - "8.25" → valid (if decimalPlaces >= 2)
   * - "8.256" → rejected if decimalPlaces = 2
   * - "8..5" → rejected (multiple decimal points)
   */
  const handleChange = (e) => {
    let val = e.target.value;

    hasUserTyped.current = true;

    // Allow empty input
    if (val === "") {
      setInputValue(val);
      onChange({ ...e, target: { ...e.target, name, value: val } });
      return;
    }

    // Conditionally allow negative sign based on allowNegative prop
    const regex = allowNegative ? /^-?[0-9]*\.?[0-9]*$/ : /^[0-9]*\.?[0-9]*$/;

    // Allow only digits and at most one decimal point while typing (and optional minus sign if allowed)
    if (regex.test(val)) {
      const parts = val.replace(/^-/, "").split("."); // Remove minus for counting decimals

      if (parts.length === 1) {
        // Just an integer (or partial), let the user keep typing
        setInputValue(val);
        onChange({ ...e, target: { ...e.target, name, value: val } });
      } else if (parts.length === 2) {
        // Has decimal point, but only enforce max decimalPlaces length
        if (parts[1].length <= decimalPlaces) {
          setInputValue(val);
          onChange({ ...e, target: { ...e.target, name, value: val } });
        }
      }
      // If more than one '.', ignore (do nothing)
    }
  };

  /**
   * handleClick - Selects all text when user clicks on the input field
   *
   * This improves UX by allowing users to quickly replace the entire value
   * by simply clicking and typing, without needing to manually select the text.
   *
   * @param {Event} e - The click event
   *
   * Behavior:
   * - Highlights all text in the input on click
   * - Next keystroke will replace the entire value
   * - Common pattern for numeric inputs to speed up data entry
   *
   * Example:
   * - Field shows "8.25"
   * - User clicks → "8.25" becomes highlighted
   * - User types "5" → field now shows "5"
   */
  const handleClick = (e) => {
    // Select all text when clicking on the input
    e.target.select();
  };

  /**
   * handleBlur - Normalizes and validates the input value when user leaves the field
   *
   * This function is called when the input loses focus. It performs final validation,
   * formatting, and normalization of the value. Partial inputs like "12." or "12.5"
   * are formatted to proper decimal notation (e.g., "12.00", "12.50").
   *
   * @param {Event} e - The blur event
   *
   * Behavior:
   * - Parses the current input value to a number
   * - Enforces minimum value of 0 (prevents negative values)
   * - Formats to 2 decimal places using numberFormat utility
   * - Updates both local state and parent state with formatted value
   * - Resets hasUserTyped flag to allow parent updates again
   * - Calls parent's onBlur handler if provided
   *
   * Example transformations on blur:
   * - "8" → "8.00"
   * - "8." → "8.00"
   * - "8.5" → "8.50"
   * - "8.25" → "8.25"
   * - "-5" → "0.00" (negative prevented)
   * - "0" → "0.00" (minimum enforced)
   */
  const handleBlur = (e) => {
    const inputVal = e.target.value;

    // Use toDecimal for safe conversion
    const decimalValue = toDecimal(inputVal);

    // Check if conversion was successful (not NaN)
    if (!decimalValue.isNaN()) {
      // Enforce minimum value of 0 using Decimal comparison (unless allowNegative is true)
      const validValue = allowNegative ? decimalValue : Decimal.max(decimalValue, 0);

      // Round to 2 decimal places using roundValue
      const rounded = roundValue(validValue, 2);
      const roundedAsString = rounded.toFixed(2); // Force 2 decimal places display

      setInputValue(roundedAsString);
      onChange({
        ...e,
        target: { ...e.target, name, value: roundedAsString },
      });
    }

    hasUserTyped.current = false;
    if (onBlur) onBlur(e);
  };

  let isRequired =
    required ||
    (validation.includes("required") &&
      !(validation.includes("skip") || validation.includes("optional")));

  return (
    <div className="form-group">
      <div className={_.uniq(["input-block", className]).join(" ")}>
        {(label || placeholder) && (
          <label className="input-label">
            <span>
              {label || placeholder}
              {isRequired ? <span className="text-danger">&nbsp;*</span> : null}
            </span>
          </label>
        )}
        <div className={`custom-number-input-wrapper ${isQuantityField && " custom-number-input-quantity-input"}`}>
          {isQuantityField && !disabled && (
            <button
              type="button"
              className="number-step-button number-step-button-down"
              onClick={() => handleStep(-1)}
              disabled={disabled}
            >
              −
            </button>
          )}

          <input
            className={_.uniq([
              "form-control-input",
              ...(disabled ? ["disabled"] : []),
              ...customInputClass.split(" "),
            ]).join(" ")}
            name={name}
            type={inputType}
            value={inputValue}
            disabled={disabled}
            onChange={handleChange}
            onBlur={handleBlur}
            onClick={handleClick}
            onWheel={(e) => e.target.blur()}
            inputMode="decimal"
            onKeyDown={disableArrows}
            data-cy={dataCy}
            {...props}
          />

          {isQuantityField && !disabled && (
            <button
              type="button"
              className="number-step-button number-step-button-up"
              onClick={() => handleStep(1)}
              disabled={disabled}
            >
              +
            </button>
          )}
        </div>
      </div>
      {!disabled && error && touched && (
        <small className="float-start text-danger">{error}</small>
      )}
    </div>
  );
}
